/**
 * A **name-only** mirror of backend/src/game/equipment/config/gear-defs.ts
 * (2026-09-04).
 *
 * User: "we need many more items that I can equip on my character — not
 * cosmetics, but actual items visible in the inventory."
 *
 * ## Why this is a stub and not the whole catalogue
 *
 * The paperdoll picker and the Shop's Gear shelf both read the *served*
 * catalogue (`GET /api/equipment/gear/catalog`), which carries the stats, the
 * copy and what this account owns — the lesson `game-modes.controller.ts`
 * draws from `ITEM_DEFS`, which was mirrored by hand and drifted.
 *
 * What cannot be served is the **in-run** loot panel: a chest can hand you a
 * Vault Cuirass three floors down, and the panel that names it has no hub
 * state and no business making a request mid-fight. So this file carries the
 * two fields that panel needs — a name and a slot — and nothing that a
 * rebalance could make wrong. A stat here would be a second source of truth;
 * a name is a label.
 */

import { N_, NC_ } from '../i18n/i18n';

export type GearSlot = 'HEAD' | 'CHEST' | 'HANDS' | 'WAIST' | 'BACK' | 'FEET' | 'TRINKET';

export interface GearLabel {
  name: string;
  slot: GearSlot;
  /** 1 Cellars, 2 Ossuary, 3 Vaults — drives the frame colour on a loot tile. */
  tier: 1 | 2 | 3;
}

export const GEAR_LABELS: Record<string, GearLabel> = {
  padded_coif: { name: N_('Padded Coif'), slot: 'HEAD', tier: 1 },
  ashen_hood: { name: N_('Ashen Pelt-Hood'), slot: 'HEAD', tier: 1 },
  warden_helm: { name: N_('Warden\'s Helm'), slot: 'HEAD', tier: 2 },
  bonepicker_hood: { name: N_('Bonepicker\'s Hood'), slot: 'HEAD', tier: 2 },
  vault_greathelm: { name: N_('Vault Greathelm'), slot: 'HEAD', tier: 3 },
  warden_hood: { name: N_('Warden\'s Hood'), slot: 'HEAD', tier: 3 },

  banded_harness: { name: N_('Banded Harness'), slot: 'CHEST', tier: 1 },
  quilted_vest: { name: N_('Quilted Vest'), slot: 'CHEST', tier: 1 },
  warden_pauldrons: { name: N_('Warden\'s Pauldrons'), slot: 'CHEST', tier: 2 },
  ossuary_wrap: { name: N_('Ossuary Wrap'), slot: 'CHEST', tier: 2 },
  vault_cuirass: { name: N_('Vault Cuirass'), slot: 'CHEST', tier: 3 },
  sealed_mantle: { name: N_('Sealed Mantle'), slot: 'CHEST', tier: 3 },

  tallow_cloak: { name: N_('Tallow Cloak'), slot: 'BACK', tier: 1 },
  porters_pack: { name: N_('Porter\'s Pack'), slot: 'BACK', tier: 1 },
  warden_cape: { name: N_('Warden\'s Cape'), slot: 'BACK', tier: 2 },
  bonepickers_pack: { name: N_('Bonepicker\'s Pack'), slot: 'BACK', tier: 2 },
  vault_mantle: { name: N_('Vault Mantle'), slot: 'BACK', tier: 3 },
  deepwalkers_pack: { name: N_('Deepwalker\'s Pack'), slot: 'BACK', tier: 3 },

  gaolers_keyring: { name: N_('Gaoler\'s Keyring'), slot: 'TRINKET', tier: 1 },
  tallow_taper: { name: N_('Tallow Taper'), slot: 'TRINKET', tier: 1 },
  bone_tally: { name: N_('Bone Tally'), slot: 'TRINKET', tier: 2 },
  emberglass_vial: { name: N_('Emberglass Vial'), slot: 'TRINKET', tier: 2 },
  vaultkeepers_key: { name: N_('Vaultkeeper\'s Key'), slot: 'TRINKET', tier: 3 },
  vault_lodestone: { name: N_('Vault Lodestone'), slot: 'TRINKET', tier: 3 },

  riveted_gloves: { name: N_('Riveted Gloves'), slot: 'HANDS', tier: 1 },
  tallow_wraps: { name: N_('Tallow Wraps'), slot: 'HANDS', tier: 1 },
  warden_gauntlets: { name: N_('Warden\'s Gauntlets'), slot: 'HANDS', tier: 2 },
  bonepicker_mitts: { name: N_('Bonepicker\'s Mitts'), slot: 'HANDS', tier: 2 },
  vault_gauntlets: { name: N_('Vault Gauntlets'), slot: 'HANDS', tier: 3 },
  deepwalkers_grips: { name: N_('Deepwalker\'s Grips'), slot: 'HANDS', tier: 3 },

  gaolers_belt: { name: N_('Gaoler\'s Belt'), slot: 'WAIST', tier: 1 },
  porters_sash: { name: N_('Porter\'s Sash'), slot: 'WAIST', tier: 1 },
  warden_girdle: { name: N_('Warden\'s Girdle'), slot: 'WAIST', tier: 2 },
  bonepickers_sash: { name: N_('Bonepicker\'s Sash'), slot: 'WAIST', tier: 2 },
  vault_girdle: { name: N_('Vault Girdle'), slot: 'WAIST', tier: 3 },
  vault_sash: { name: N_('Vault Sash'), slot: 'WAIST', tier: 3 },

  nailed_boots: { name: N_('Nailed Boots'), slot: 'FEET', tier: 1 },
  felted_boots: { name: N_('Felted Boots'), slot: 'FEET', tier: 1 },
  warden_sabatons: { name: N_('Warden\'s Sabatons'), slot: 'FEET', tier: 2 },
  ossuary_striders: { name: N_('Ossuary Striders'), slot: 'FEET', tier: 2 },
  vault_sabatons: { name: N_('Vault Sabatons'), slot: 'FEET', tier: 3 },
  deepwalkers_treads: { name: N_('Deepwalker\'s Treads'), slot: 'FEET', tier: 3 },

  // The Chandlery — Ember-only variants of the tier 3 pieces, each in its
  // base piece's slot. Missing here until 2026-10-16, and this table is also
  // what `worn-gear.ts` reads a slot from, so a bought Chandler's Crown was
  // silently never drawn on the delver. `gear-defs.test.ts` keeps the two
  // tables level now.
  chandlers_crown: { name: N_('Chandler\'s Crown'), slot: 'HEAD', tier: 3 },
  chandlers_shroud: { name: N_('Chandler\'s Shroud'), slot: 'CHEST', tier: 3 },
  chandlers_mantle: { name: N_('Chandler\'s Mantle'), slot: 'BACK', tier: 3 },
  chandlers_seal: { name: N_('Chandler\'s Seal'), slot: 'TRINKET', tier: 3 },
  chandlers_gloves: { name: N_('Chandler\'s Gloves'), slot: 'HANDS', tier: 3 },
  chandlers_sash: { name: N_('Chandler\'s Sash'), slot: 'WAIST', tier: 3 },
  chandlers_boots: { name: N_('Chandler\'s Boots'), slot: 'FEET', tier: 3 },
};

/** True when an id off the wire is a piece of gear rather than a curio. */
export function isGearId(itemDefId: string): boolean {
  return Object.hasOwn(GEAR_LABELS, itemDefId);
}

export function gearLabel(itemDefId: string): GearLabel | undefined {
  return GEAR_LABELS[itemDefId];
}

/**
 * What the slots are called, in the order the paperdoll reads them. Drawn
 * with `tc('gear slot', label)` — "Back" the slot is not "Back" the button.
 */
export const GEAR_SLOT_LABEL: Record<GearSlot, string> = {
  HEAD: NC_('gear slot', 'Head'), CHEST: NC_('gear slot', 'Chest'), HANDS: NC_('gear slot', 'Hands'), WAIST: NC_('gear slot', 'Waist'),
  BACK: NC_('gear slot', 'Back'), FEET: NC_('gear slot', 'Feet'), TRINKET: NC_('gear slot', 'Trinket'),
};
